import { useRef, useCallback } from 'react';

export interface LongPressOptions {
  /** Milliseconds to hold before triggering. Default: 550 */
  delay?: number;
  /** Max pixel drift allowed before cancelling. Default: 10 */
  moveThreshold?: number;
  /** Called when long-press threshold is reached */
  onLongPress: (x: number, y: number) => void;
  /** Whether the long-press is enabled at all */
  enabled?: boolean;
}

export interface LongPressHandlers {
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchCancel: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  /** True when a long-press was the last detected interaction.
   *  Call clearTriggered() after you consume it so next normal clicks work. */
  triggered: () => boolean;
  clearTriggered: () => void;
}

/**
 * Reusable long-press hook that works on both mouse and touch.
 * Cancels if the pointer moves more than moveThreshold pixels.
 * When triggered, suppresses the next click event.
 */
export function useLongPress({
  delay = 550,
  moveThreshold = 10,
  onLongPress,
  enabled = true,
}: LongPressOptions): LongPressHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const triggeredRef = useRef(false);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPosRef.current = null;
  };

  const start = useCallback(
    (x: number, y: number) => {
      if (!enabled) return;
      clear();
      triggeredRef.current = false;
      startPosRef.current = { x, y };
      timerRef.current = setTimeout(() => {
        triggeredRef.current = true;
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate?.(25);
        }
        onLongPress(x, y);
      }, delay);
    },
    [enabled, delay, onLongPress],
  );

  const checkMove = useCallback(
    (x: number, y: number) => {
      if (!startPosRef.current || !timerRef.current) return;
      const dx = x - startPosRef.current.x;
      const dy = y - startPosRef.current.y;
      if (dx * dx + dy * dy > moveThreshold * moveThreshold) {
        clear();
      }
    },
    [moveThreshold],
  );

  return {
    onMouseDown: (e) => start(e.clientX, e.clientY),
    onMouseUp: () => clear(),
    onMouseMove: (e) => checkMove(e.clientX, e.clientY),
    onMouseLeave: () => clear(),

    onTouchStart: (e) => {
      const t = e.touches[0];
      if (t) start(t.clientX, t.clientY);
    },
    onTouchEnd: () => clear(),
    onTouchCancel: () => clear(),
    onTouchMove: (e) => {
      const t = e.touches[0];
      if (t) checkMove(t.clientX, t.clientY);
    },

    triggered: () => triggeredRef.current,
    clearTriggered: () => { triggeredRef.current = false; },
  };
}
